<script setup lang="ts">
import { computed, ref } from 'vue'

import MediaImage from '@fondof-ui/components/media/MediaImage.vue'
import MediaRoot from '@fondof-ui/components/media/MediaRoot.vue'
import { contentfulUrl } from '@internal/format-contentful'
import Button from '@satch-ui/components/button/Button.vue'
import HeroContent from '@storefront-satch/components/molecules/hero/HeroContent.vue'
import HeroRoot from '@storefront-satch/components/molecules/hero/HeroRoot.vue'
import type { BrandHero } from '@storefront-satch/components/molecules/hero/types'
import MediaVideo from '@storefront-satch/components/molecules/media/MediaVideo.vue'
import { clsx } from 'clsx'

import { ArrowRightIcon, PlayIcon } from '@fond-of/satch-icons'

interface HeroFullProps extends BrandHero {
  /**
   * The Media type is used to differentiate between video and image to enable certain behaviours
   */
  mediaType?: 'image' | 'video' | 'additionalVideo' | null
}

const props = defineProps<HeroFullProps>()
const hasAdditionalImage = !!props.additionalImage
const isVideoPlaying = ref<boolean>(false)

const isLinkElement = computed(() => {
  return props.link && !isVideoWithoutAutoplay.value
})

const isVideoWithoutAutoplay = computed(() => {
  return props.mediaType === 'video' && !props.media?.videoAutoplay
})

const hasVideo = computed(() => {
  return props.mediaType === 'video' || props.mediaType === 'additionalVideo'
})
</script>
<template>
  <HeroRoot
    v-bind="props"
    data-label="Hero"
    :hero-id="props.sys?.id"
    :class="[
      'h-[calc(90svh-55px)] max-h-[161.12vw] min-h-[135vw] w-full',
      'aspect-[2/1] sm:h-auto sm:max-h-none sm:min-h-0',
    ]"
    :has-additional-image="hasAdditionalImage"
  >
    <component
      :is="isLinkElement ? 'a' : 'div'"
      data-label="Hero Inner"
      :href="isLinkElement ? contentfulUrl(props.link) : undefined"
      :class="[
        'relative h-full min-h-[135vw] w-full sm:min-h-0',
        'flex flex-col flex-nowrap sm:flex-row',
        {
          'justify-between sm:items-center': hasAdditionalImage,
          'justify-end sm:justify-between': !hasAdditionalImage,
        },
      ]"
      :data-gtm-attr="isLinkElement ? 'button_hero' : undefined"
      :data-gtm-label="isLinkElement ? props.internalTitle : undefined"
      :aria-label="
        isLinkElement
          ? props.linkAriaLabel?.trim() || props.linkText?.trim() || undefined
          : undefined
      "
    >
      <div
        v-if="props.media"
        data-label="Hero Image"
        :class="
          clsx('h-full min-h-[135vw] w-full self-start sm:min-h-0', {
            relative: !hasAdditionalImage,
            absolute: hasAdditionalImage,
          })
        "
      >
        <MediaRoot
          :entry="props.media"
          class="media absolute left-0 top-0 h-full w-full object-cover object-top"
        >
          <template #video="{ data }">
            <MediaVideo
              v-bind="data"
              class="h-full w-full"
              :show-play-button="
                !(props.linkText && (props.link || isVideoWithoutAutoplay))
              "
              @update:video-playing="
                (state) => {
                  isVideoPlaying = state
                }
              "
            />
          </template>
          <template #image="{ data }">
            <MediaImage
              v-bind="data"
              cover-image
              :divider-mobile="2"
              :divider-tablet="1.5"
              :divider-desktop="1"
              lazyload="eager"
              fetchpriority="high"
            />
          </template>
        </MediaRoot>
      </div>
      <div
        data-label="content wrapper"
        :class="
          clsx('md:pl-15 mb-8 w-full pl-5 pr-5 sm:mb-0 sm:w-1/2 sm:pl-10', {
            'sm:w-full md:w-3/4': props.headlineSize === 'super',
            'sm:bottom-none sm:order-auto relative order-2 sm:max-w-[55%]':
              hasAdditionalImage,
            'absolute sm:bottom-10 lg:bottom-[130px]': !hasAdditionalImage,
            'pointer-events-none':
              (mediaType === 'video' && !props.media?.videoAutoplay) ||
              !props.link,
          })
        "
      >
        <div
          v-if="props.content"
          :class="[
            'select-none transition-opacity duration-100',
            {
              'opacity-1': mediaType === 'image',
              'cursor-pointer':
                mediaType === 'video' && !props.media?.videoAutoplay,
              'opacity-0':
                mediaType === 'video' &&
                isVideoPlaying === true &&
                !props.media?.videoAutoplay,
            },
          ]"
        >
          <HeroContent
            :content="props.content"
            :content-style="props.contentStyle"
            :has-additional-image="hasAdditionalImage"
            :headline-size="props.headlineSize"
            :headline-style="props.headlineStyle"
            :class-headline="{
              'text-3xl sm:text-2xl md:text-3xl':
                !hasAdditionalImage && props.headlineSize !== 'super',
              'text-xl sm:text-lg md:text-2xl':
                hasAdditionalImage && props.headlineSize !== 'super',
              'text-4xl': props.headlineSize === 'super',
            }"
          />
          <div
            v-if="props.linkStyle && props.linkText"
            class="mt-4 w-full"
          >
            <Button
              v-if="props.linkText && (props.link || isVideoWithoutAutoplay)"
              class="font-sans-medium font-semibold"
              :variant="props.linkStyle"
              padding="large"
              text-size="large"
            >
              {{ props.linkText }}
              <PlayIcon
                v-if="isVideoWithoutAutoplay && hasVideo"
                class="w-4"
              />
              <ArrowRightIcon
                v-else
                class="w-4"
              />
            </Button>
          </div>
        </div>
      </div>
      <div
        v-if="hasAdditionalImage"
        :class="
          clsx(
            'relative z-[1] mb-4 aspect-square h-1/2 self-center',
            'sm:absolute sm:right-0 sm:top-1/2 sm:h-4/5 sm:-translate-y-1/2 sm:self-auto',
            'sm:right-[60px] md:right-[80px] lg:right-[100px]'
          )
        "
      >
        <MediaRoot
          :entry="props.additionalImage"
          class="relative h-full w-full"
        >
          <template #video="{ data }">
            <MediaVideo
              v-bind="data"
              class="h-full w-full"
              @update:video-playing="
                (state) => {
                  isVideoPlaying = state
                }
              "
            />
          </template>
          <template #image="{ data }">
            <MediaImage
              v-bind="data"
              class="absolute left-0 top-0 w-full"
              :divider-mobile="2"
              :divider-tablet="2"
              :divider-desktop="1.5"
              lazyload="eager"
              fetchpriority="high"
            />
          </template>
        </MediaRoot>
      </div>
    </component>
  </HeroRoot>
</template>
